/**
 * DebugSettings - Debug overlay management utilities
 *
 * Consolidated from OverlaySystem.ts (moved from /overlay/ folder).
 * Extracted from EdgeDocsCanvas.V3.tsx for better modularity and testability.
 *
 * This utility provides functions for managing debug overlay settings,
 * persistence, and communication with external debug systems.
 */

/**
 * Complete debug settings interface
 * This is the single source of truth for all debug overlay settings.
 */
export interface DebugSettings {
  // Visual overlays
  showCaretMap: boolean;
  showGlyphBoxes: boolean;
  showLineBoxes: boolean;
  showBaselines: boolean;
  showWhitespaceBadges: boolean;
  showBreakMarkers: boolean;
  showDebugDashboard: boolean;
  // Incremental layout monitoring
  showIncrementalLogs: boolean;
  showDeltaDetails: boolean;
  showSnapshotDiff: boolean;
  highlightAffectedParagraphs: boolean;
}

/**
 * Legacy type alias for backwards compatibility
 * @deprecated Use DebugSettings instead
 */
export type OverlaySettings = DebugSettings;

export interface DebugSystemDependencies {
  overlaySettingsRef: React.MutableRefObject<DebugSettings>;
  drawOverlays?: () => void;
}

/**
 * Legacy type alias for backwards compatibility
 * @deprecated Use DebugSystemDependencies instead
 */
export type OverlaySystemDependencies = DebugSystemDependencies;

/**
 * Default debug settings
 */
export const DEFAULT_DEBUG_SETTINGS: DebugSettings = {
  // Visual overlays
  showCaretMap: false,
  showGlyphBoxes: false,
  showLineBoxes: false,
  showBaselines: false,
  showWhitespaceBadges: false,
  showBreakMarkers: false,
  showDebugDashboard: false,
  // Incremental layout monitoring
  showIncrementalLogs: false,
  showDeltaDetails: false,
  showSnapshotDiff: false,
  highlightAffectedParagraphs: false,
};

/**
 * Legacy export for backwards compatibility
 * @deprecated Use DEFAULT_DEBUG_SETTINGS instead
 */
export const DEFAULT_OVERLAY_SETTINGS = DEFAULT_DEBUG_SETTINGS;

/**
 * Update debug settings from external event
 */
export function updateDebugSettingsFromEvent(
  deps: DebugSystemDependencies,
  eventDetail: any
): void {
  const { overlaySettingsRef } = deps;

  overlaySettingsRef.current = {
    // Visual overlays
    showCaretMap: !!eventDetail.showCaretMap,
    showGlyphBoxes: !!eventDetail.showGlyphBoxes,
    showLineBoxes: !!eventDetail.showLineBoxes,
    showBaselines: !!eventDetail.showBaselines,
    showWhitespaceBadges: !!eventDetail.showWhitespaceBadges,
    showBreakMarkers: !!eventDetail.showBreakMarkers,
    showDebugDashboard: !!eventDetail.showDebugDashboard,
    // Incremental layout monitoring
    showIncrementalLogs: !!eventDetail.showIncrementalLogs,
    showDeltaDetails: !!eventDetail.showDeltaDetails,
    showSnapshotDiff: !!eventDetail.showSnapshotDiff,
    highlightAffectedParagraphs: !!eventDetail.highlightAffectedParagraphs,
  };

  // Trigger overlay redraw if function is available
  try {
    deps.drawOverlays?.();
  } catch (error) {
    console.warn('Failed to redraw overlays:', error);
  }
}

/**
 * Legacy function alias for backwards compatibility
 * @deprecated Use updateDebugSettingsFromEvent instead
 */
export const updateOverlaySettingsFromEvent = updateDebugSettingsFromEvent;

/**
 * Load debug settings from localStorage
 */
export function loadDebugSettingsFromStorage(): Partial<DebugSettings> | null {
  try {
    const stored = localStorage.getItem('edgedocs_overlay_settings');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to load debug settings from localStorage:', error);
    return null;
  }
}

/**
 * Legacy function alias for backwards compatibility
 * @deprecated Use loadDebugSettingsFromStorage instead
 */
export const loadOverlaySettingsFromStorage = loadDebugSettingsFromStorage;

/**
 * Load debug settings from window object (backwards compatibility)
 */
export function loadDebugSettingsFromWindow(): Partial<DebugSettings> | null {
  try {
    const persisted = (window as any).__edg_overlaySettings;
    return persisted ? {
      showCaretMap: !!persisted.showCaretMap,
      showGlyphBoxes: !!persisted.showGlyphBoxes,
      showLineBoxes: !!persisted.showLineBoxes,
      showBaselines: !!persisted.showBaselines,
      showWhitespaceBadges: !!persisted.showWhitespaceBadges,
      showBreakMarkers: !!persisted.showBreakMarkers,
      showDebugDashboard: !!persisted.showDebugDashboard,
      showIncrementalLogs: !!persisted.showIncrementalLogs,
      showDeltaDetails: !!persisted.showDeltaDetails,
      showSnapshotDiff: !!persisted.showSnapshotDiff,
      highlightAffectedParagraphs: !!persisted.highlightAffectedParagraphs,
    } : null;
  } catch (error) {
    console.warn('Failed to load debug settings from window:', error);
    return null;
  }
}

/**
 * Legacy function alias for backwards compatibility
 * @deprecated Use loadDebugSettingsFromWindow instead
 */
export const loadOverlaySettingsFromWindow = loadDebugSettingsFromWindow;

/**
 * Initialize debug settings from storage and window
 */
export function initializeDebugSettings(
  deps: DebugSystemDependencies
): () => void {
  const { overlaySettingsRef } = deps;

  // Try to load from localStorage first
  const storedSettings = loadDebugSettingsFromStorage();
  if (storedSettings) {
    overlaySettingsRef.current = { ...DEFAULT_DEBUG_SETTINGS, ...storedSettings };
  } else {
    // Fallback to window for backwards compatibility
    const windowSettings = loadDebugSettingsFromWindow();
    if (windowSettings) {
      overlaySettingsRef.current = { ...DEFAULT_DEBUG_SETTINGS, ...windowSettings };
    }
  }

  // Setup event listener for dynamic updates
  const eventHandler = (e: Event) => {
    const ce = e as CustomEvent;
    updateDebugSettingsFromEvent(deps, ce.detail);
  };

  window.addEventListener('edgedocs:overlay-settings' as any, eventHandler);

  // Return cleanup function
  return () => {
    window.removeEventListener('edgedocs:overlay-settings' as any, eventHandler);
  };
}

/**
 * Legacy function alias for backwards compatibility
 * @deprecated Use initializeDebugSettings instead
 */
export const initializeOverlaySettings = initializeDebugSettings;

/**
 * Create debug settings change handler
 */
export function createDebugSettingsHandler(
  deps: DebugSystemDependencies
) {
  return (e: Event) => {
    const ce = e as CustomEvent;
    updateDebugSettingsFromEvent(deps, ce.detail);
  };
}

/**
 * Legacy function alias for backwards compatibility
 * @deprecated Use createDebugSettingsHandler instead
 */
export const createOverlaySettingsHandler = createDebugSettingsHandler;
